
import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { FormInput } from "@/components/FormFields/FormInput";
import { FormSelect } from "@/components/FormFields/FormSelect";
import { FormTextarea } from "@/components/FormFields/FormTextarea";
import { FormCheckbox } from "@/components/FormFields/FormCheckbox";
import { reportFormSchema, ReportFormData, interestOptions } from "./formSchema";

interface ReportFormProps {
  onSubmit: (data: ReportFormData) => void;
  isSubmitting: boolean;
}

export function ReportForm({ onSubmit, isSubmitting }: ReportFormProps) {
  const { toast } = useToast();
  const form = useForm<ReportFormData>({
    resolver: zodResolver(reportFormSchema),
    defaultValues: {
      fullName: "",
      email: "",
      companyName: "",
      jobTitle: "",
      primaryInterest: "",
      challenge: "",
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="mb-6 text-center">
          <div className="mx-auto w-32 h-12 bg-white/5 rounded-md mb-4 flex items-center justify-center">
            <span className="text-primary font-bold">Docket Labs</span>
          </div>
          <h2 className="text-xl font-semibold">Download Our Legal AI Success Stories Report</h2>
        </div>

        <FormInput
          form={form}
          name="fullName"
          label="Full Name"
          placeholder="Enter your full name"
        />

        <FormInput
          form={form}
          name="email"
          label="Work Email"
          placeholder="Enter your work email"
          type="email"
        />

        <FormInput
          form={form}
          name="companyName"
          label="Company Name"
          placeholder="Enter your company name"
        />

        <FormInput
          form={form}
          name="jobTitle"
          label="Job Title"
          placeholder="Enter your job title"
        />

        <FormSelect
          form={form}
          name="primaryInterest"
          label="Primary Interest"
          options={interestOptions}
        />

        <FormTextarea
          form={form}
          name="challenge"
          label="What is your biggest legal AI challenge?"
          placeholder="Briefly describe your challenge (optional)"
          maxLength={500}
          showCount={true}
        />

        <FormCheckbox
          form={form}
          name="consent"
        >
          I agree to receive updates from Docket Labs and acknowledge the{" "}
          <a 
            href="https://docketlabs.com/privacy" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Privacy Policy
          </a>.
        </FormCheckbox>
        
        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Processing..." : "Get the Report"}
        </Button>
      </form>
    </Form>
  );
}
